import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './styles/ImageCarousel.module.css';

/**
 * ImageCarousel
 *
 * A single, reusable carousel component for use across pages
 * (Gallery, GetInvolved, Home, etc.)
 *
 * Props:
 * - images: Array<{ src: string, alt?: string }> | Array<string>  (required)
 * - title: string (optional) -> rendered centered over the image(s)
 * - autoPlay: boolean (optional, default true) -> only applies when there is more than 1 image
 * - interval: number (optional, default 4000ms) -> autoplay delay
 * - height: string (optional, default '420px') -> CSS height of the carousel
 *
 * Behaviour:
 * - If only ONE image is passed -> no arrows, no dots, no sliding. Just the image (+ optional title).
 * - If MORE than one image is passed -> full carousel with arrows, dots and (optional) autoplay.
 */
const ImageCarousel = ({
  images = [],
  title,
  autoPlay = true,
  interval = 4000,
  height = '520px',
}) => {
  // normalize images so both ["url1", "url2"] and [{src, alt}] work
  const normalized = images
    .filter(Boolean)
    .map((img) =>
      typeof img === 'string' ? { src: img, alt: title || 'carousel image' } : img
    );

  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [loaded, setLoaded] = useState({});

  const isSingleImage = normalized.length <= 1;

  // Preload every image up front so slides never show a blank/loading frame
  // when the user (or autoplay) moves to them.
  useEffect(() => {
    normalized.forEach((img, idx) => {
      const preloader = new Image();
      preloader.src = img.src;
      preloader.onload = () => {
        setLoaded((prev) => (prev[idx] ? prev : { ...prev, [idx]: true }));
      };
      // if it's already cached, onload may not fire reliably in some browsers
      if (preloader.complete) {
        setLoaded((prev) => (prev[idx] ? prev : { ...prev, [idx]: true }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  const goTo = useCallback(
    (index) => {
      if (normalized.length === 0) return;
      const nextIndex = (index + normalized.length) % normalized.length;
      setDirection(nextIndex > current ? 1 : -1);
      setCurrent(nextIndex);
    },
    [current, normalized.length]
  );

  const goNext = useCallback(() => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % normalized.length);
  }, [normalized.length]);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + normalized.length) % normalized.length);
  }, [normalized.length]);

  useEffect(() => {
    if (isSingleImage || !autoPlay) return undefined;
    const timer = setInterval(goNext, interval);
    return () => clearInterval(timer);
  }, [isSingleImage, autoPlay, interval, goNext]);

  if (normalized.length === 0) {
    return null;
  }

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0 }),
  };

  return (
    <div className={styles.carouselWrapper} style={{ height }}>
      {/* ---- Single image mode: no controls, no scrolling ---- */}
      {isSingleImage ? (
        <div className={styles.slide}>
          <img
            src={normalized[0].src}
            alt={normalized[0].alt}
            fetchpriority="high"
            className={`${styles.image} ${loaded[0] ? styles.imageLoaded : ''}`}
          />
          {title && (
            <div className={styles.titleOverlay}>
              <h2 className={styles.title}>{title}</h2>
            </div>
          )}
        </div>
      ) : (
        <>
          <AnimatePresence initial={false} custom={direction} mode="sync">
            <motion.div
              key={current}
              className={styles.slide}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.5, ease: 'easeInOut' }}
            >
              <img
                src={normalized[current].src}
                alt={normalized[current].alt}
                fetchpriority="high"
                className={`${styles.image} ${loaded[current] ? styles.imageLoaded : ''}`}
              />
            </motion.div>
          </AnimatePresence>

          {title && (
            <div className={styles.titleOverlay}>
              <h2 className={styles.title}>{title}</h2>
            </div>
          )}

          <button
            className={`${styles.arrow} ${styles.arrowLeft}`}
            onClick={goPrev}
            aria-label="Previous image"
            type="button"
          >
            &#10094;
          </button>
          <button
            className={`${styles.arrow} ${styles.arrowRight}`}
            onClick={goNext}
            aria-label="Next image"
            type="button"
          >
            &#10095;
          </button>

          <div className={styles.dots}>
            {normalized.map((_, idx) => (
              <button
                key={idx}
                className={`${styles.dot} ${idx === current ? styles.dotActive : ''}`}
                onClick={() => goTo(idx)}
                aria-label={`Go to image ${idx + 1}`}
                type="button"
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ImageCarousel;